/**
 * First-run profile setup.
 *
 * We deliberately do NOT trust the identity provider (Microsoft Entra External
 * ID) to give us a usable display name — the user names themselves inside OUR
 * app on first login. This warm welcome step asks "how should we call you?",
 * pre-filled with whatever name we currently have, and PATCHes it via
 * `api.updateMe({ displayName })`.
 *
 * Gating lives in `AuthedApp` (a localStorage flag); this screen only owns the
 * form. On a successful save it `reloadMe()`s so the new name propagates, then
 * calls `onDone()` to advance the flow.
 *
 * Client-side validation mirrors the shared `updateProfileInputSchema` (trim,
 * 1..120 chars); the server re-validates and is the source of truth.
 */
import { useState, type FormEvent } from 'react';
import { ApiClientError } from '@solandia/core';
import { useI18n } from '../i18n';
import { useApi } from '../api/ApiProvider';
import { Wordmark } from '../components/Logo';
import { Field, Form } from '../components/ui';

const MAX_NAME = 120;

export function ProfileSetupScreen({
  initialName,
  reloadMe,
  onDone,
}: {
  /** Current best-effort display name (me.user.displayName / session). */
  initialName: string;
  /** Refetch `auth/me` so the new name propagates across the app. */
  reloadMe: () => void;
  /** Mark setup done and advance the flow (sets the localStorage flag). */
  onDone: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const trimmed = name.trim();
    if (trimmed.length < 1 || trimmed.length > MAX_NAME) {
      setError(t.profileSetup.nameInvalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.updateMe({ displayName: trimmed });
      // Propagate the new name everywhere `me.user` is read (top bar, menu, …).
      reloadMe();
      // Persist the "done" flag and advance, so this never repeats.
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiClientError && err.code === 'unprocessable_entity'
          ? t.profileSetup.nameInvalid
          : t.errors.generic,
      );
      setBusy(false);
    }
  }

  return (
    <main className="auth-screen">
      <div className="card auth-card" style={{ textAlign: 'left' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Wordmark size={32} />
        </div>
        <h1 style={{ fontSize: '1.6rem', textAlign: 'center' }}>
          {t.profileSetup.title}
        </h1>
        <p className="lead" style={{ margin: '6px 0 20px', textAlign: 'center' }}>
          {t.profileSetup.lead}
        </p>
        <Form onSubmit={submit}>
          <Field
            label={t.profileSetup.nameLabel}
            hint={t.profileSetup.nameHint}
            error={error ?? undefined}
          >
            {({ id, invalid }) => (
              <input
                id={id}
                className="input"
                type="text"
                value={name}
                maxLength={MAX_NAME}
                autoComplete="name"
                aria-invalid={invalid}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                placeholder={t.profileSetup.namePlaceholder}
                autoFocus
              />
            )}
          </Field>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={busy}
          >
            {busy ? t.common.saving : t.profileSetup.submit}
          </button>
        </Form>
      </div>
    </main>
  );
}
